// name-mapping.ts

import {log} from "./logger";

// Helper function to get display name for a queue
export function getQueueDisplayName(queueName: string): string {
    log.debug('DEBUG getQueueDisplayName - input:', queueName);

    // First check if it's already a direct mapping
    if (QUEUE_BASE_NAME_MAPPING[queueName]) {
        log.debug('DEBUG getQueueDisplayName - direct match found:', QUEUE_BASE_NAME_MAPPING[queueName]);
        return QUEUE_BASE_NAME_MAPPING[queueName];
    }

    // Extract the base queue name from the full queue name
    // Flexible pattern to handle different client prefixes and environment formats
    // Patterns supported:
    // - <client>-<environment>-<number>-<queue_base_name>
    // - <client>-<environment>-<queue_base_name>
    // - <environment>-<queue_base_name> (legacy)

    let match;
    let baseQueueName;

    // Try pattern: client-env-number-queue_name (e.g., devk-dev-1-hitl-classification-rb)
    match = queueName.match(/^[^-]+-[^-]+-\d+-(.+)$/);
    if (match) {
        baseQueueName = match[1];
        log.debug('DEBUG getQueueDisplayName - pattern 1 match:', baseQueueName);
    } else {
        // Try pattern: client-env-queue_name (e.g., devk-dev-hitl-classification-rb)
        match = queueName.match(/^[^-]+-[^-]+-(.+)$/);
        if (match) {
            baseQueueName = match[1];
            log.debug('DEBUG getQueueDisplayName - pattern 2 match:', baseQueueName);
        } else {
            // Try pattern: env-queue_name (legacy format)
            match = queueName.match(/^[^-]+-(.+)$/);
            if (match) {
                baseQueueName = match[1];
                log.debug('DEBUG getQueueDisplayName - pattern 3 match:', baseQueueName);
            } else {
                // No pattern matched, check if it's already a base queue name
                baseQueueName = queueName;
                log.debug('DEBUG getQueueDisplayName - no pattern match, using original:', baseQueueName);
            }
        }
    }

    // Always apply friendly names if available, regardless of environment
    const friendlyName = QUEUE_BASE_NAME_MAPPING[baseQueueName];
    log.debug('DEBUG getQueueDisplayName - baseQueueName:', baseQueueName, 'friendlyName:', friendlyName);
    if (friendlyName) {
        return friendlyName;
    }

    // For unmapped queues, return the original name
    log.debug('DEBUG getQueueDisplayName - no mapping found, returning original:', queueName);
    return queueName;
}

// Base queue name mappings (without environment prefixes)
const QUEUE_BASE_NAME_MAPPING: Record<string, string> = {
    // Full queue names (with hitl- prefix)
    "hitl-classification-business-area": "Intelligente Weiche",
    "hitl-classification-rb": "Dokumentenklassifikation RS-Betrieb",
    "hitl-data-extraction-rb": "Datenextraktion RS-Betrieb",
    "hitl-data-extraction-rb-kuendigung": "Datenextraktion RB Kündigung",
    // RS queues (from locals.tf)
    "hitl-classification-rs": "Dokumentenklassifikation RS-Schaden (Neu- vs. Folgeschaden)",
    "hitl-splitting-rs-existing-case": "Dokumententrennung RS-Schaden (Folgeschaden)",
    "hitl-data-extraction-rs-existing-case": "Datenextraktion RS-Schaden (Folgeschaden)",
    "hitl-classification-rs-new-case": "Dokumentenklassifikation RS-Schaden (Neuschaden)",
    "hitl-data-extraction-rs-new-case-master": "Datenextraktion RS-Schaden (Neuschaden)",
    // SHUK queues
    "hitl-data-extraction-shuk-towing-invoice": "Datenextraktion SHUK Abschlepprechnung",
    // Leben-Betrieb (LB) queues
    "hitl-classification-lb": "Dokumentenklassifikation Leben-Betrieb",
    "hitl-data-extraction-lb": "Datenextraktion Leben-Betrieb",
    // Leben-Leistung (LL) queues
    "hitl-classification-ll": "Dokumentenklassifikation Leben-Leistung",
    "hitl-data-extraction-ll": "Datenextraktion Leben-Leistung",
    // PubSub queue identifiers (without hitl- prefix)
    "classification_business_area": "Intelligente Weiche",
    "classification_rb": "Dokumentenklassifikation RS-Betrieb",
    "data_extraction_rb": "Datenextraktion RS-Betrieb",
    "data_extraction_rb_kuendigung": "Datenextraktion RB Kündigung",
}

// Legacy mapping for backward compatibility (if needed)
export const QUEUE_NAME_MAPPING: Record<string, string> = {};

export function showClearingButtonForQueue(queueName: keyof typeof QUEUE_NAME_MAPPING): boolean {
  return ![
    "hitl-classification-business-area",
    "classification_business_area"
  ].includes(queueName);
}
